/**
 * Utility functions for manipulating subdomains in URLs
 */

/**
 * Adds a subdomain to a URL
 * @param url - The URL to modify
 * @param toSubdomain - The subdomain to add (null to return original URL)
 * @returns The URL with the added subdomain
 */
export const addSubdomain = (
  url: string,
  toSubdomain: string | null
): string => {
  if (toSubdomain === null) {
    return url
  }

  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `http://${url}` // Add http if no protocol is present
  }

  const resultURL = new URL(url)
  resultURL.hostname = `${toSubdomain}.${resultURL.hostname}`

  return resultURL.href
}

/**
 * Removes the first subdomain from a URL
 * @param url - The URL to modify
 * @returns The URL with the first subdomain removed
 */
export const removeSubdomain = (url: string): string => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `http://${url}`
  }

  const resultURL = new URL(url)
  const hostParts = resultURL.hostname.split('.')

  // If there are more than 2 parts (subdomain.domain.tld), remove the first part
  if (hostParts.length > 2) {
    hostParts.shift()
    resultURL.hostname = hostParts.join('.')
  }

  return resultURL.href
}

/**
 * Replaces the first subdomain in a URL with a new one
 * @param url - The URL to modify
 * @param newSubdomain - The new subdomain to use
 * @returns The URL with the replaced subdomain
 */
export const replaceSubdomain = (url: string, newSubdomain: string): string => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `http://${url}`
  }

  const resultURL = new URL(url)
  const hostParts = resultURL.hostname.split('.')

  if (hostParts.length > 2) {
    // Replace existing subdomain
    hostParts[0] = newSubdomain
  } else {
    // Add subdomain if none exists
    hostParts.unshift(newSubdomain)
  }

  resultURL.hostname = hostParts.join('.')
  return resultURL.href
}

/**
 * Extracts the first subdomain from a URL
 * @param url - The URL to extract from
 * @returns The subdomain or null if none exists
 */
export const extractSubdomain = (url: string): string | null => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `http://${url}`
  }

  const resultURL = new URL(url)
  const hostParts = resultURL.hostname.split('.')

  // If there are more than 2 parts, the first is the subdomain
  if (hostParts.length > 2) {
    return hostParts[0]
  }

  return null
}

/**
 * Checks if a URL has a subdomain
 * @param url - The URL to check
 * @returns True if the URL has a subdomain, false otherwise
 */
export const hasSubdomain = (url: string): boolean => {
  return extractSubdomain(url) !== null
}

/**
 * Gets all subdomains from a URL (for multi-level subdomains)
 * @param url - The URL to extract from
 * @returns Array of subdomains or empty array if none exist
 */
export const extractAllSubdomains = (url: string): string[] => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `http://${url}`
  }

  const resultURL = new URL(url)
  const hostParts = resultURL.hostname.split('.')

  // If there are more than 2 parts, everything except the last 2 are subdomains
  if (hostParts.length > 2) {
    return hostParts.slice(0, -2)
  }

  return []
}

/**
 * Normalizes a URL by ensuring it has a protocol
 * @param url - The URL to normalize
 * @param defaultProtocol - The protocol to use if none is present (default: 'https')
 * @returns The normalized URL
 */
export const normalizeUrl = (
  url: string,
  defaultProtocol: string = 'https'
): string => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `${defaultProtocol}://${url}`
  }
  return url
}
